import { useState } from 'react';
import { useStore } from 'hooks';
import { splitPhone } from 'utils';
import { IPhone } from 'types';
import { message } from 'components/ui';

export const usePhoneContacts = (id: string, phones: IPhone[]) => {
  const { createRequest } = useStore();
  const {
    applicantType,
    thirdPartyType,
    setApplicantContacts,
    setEmployeeContacts,
    setOtherEmployeeContacts,
    setOtherPersonContacts,
  } = createRequest.applicant;

  const [deletingPhone, setDeletingPhone] = useState<string | null>(null);

  const canDelete = phones.length > 1;

  const saveContacts = (params: { [key: string]: any }): Promise<any> => {
    if (applicantType === 'other' && thirdPartyType === 'other_employee') {
      return setOtherEmployeeContacts(id, params);
    }

    if (applicantType === 'other' && thirdPartyType === 'other_person') {
      return setOtherPersonContacts(id, params);
    }

    if (applicantType === 'employee') {
      return setEmployeeContacts(id, params);
    }

    return setApplicantContacts(id, params);
  };

  const buildPhonePayload = (updatedPhones: IPhone[]) => {
    if (applicantType === 'other') {
      return {
        phone_numbers: updatedPhones.map((p) => ({
          add: null,
          _type: 'cell',
          number: p.code + p.number,
        })),
      };
    }

    return {
      phones: updatedPhones.map((p) => ({
        ...p,
        add: null,
        added_by_tenant: false,
        id: p.id ?? null,
        not_actual: false,
        phone_type: p.phone_type ?? 'cell',
        str_number: p.code + p.number,
      })),
    };
  };

  const isPhoneInList = (phoneDigits: string): boolean =>
    phones.some((p) => (p.code + p.number).replace(/\D/g, '') === phoneDigits);

  const persistNewPhone = (phoneDigits: string): Promise<any> => {
    const parsed = splitPhone(phoneDigits);
    const updatedPhones: IPhone[] = [
      ...phones,
      {
        add: null,
        added_by_tenant: false,
        code: parsed.code,
        number: parsed.number,
        id: null,
        not_actual: false,
        phone_type: 'cell',
        str_number: phoneDigits,
      } as IPhone,
    ];

    return saveContacts(buildPhonePayload(updatedPhones));
  };

  const removePhoneNumber = async (phone: IPhone) => {
    const phoneKey = phone.code + phone.number;

    if (!canDelete || deletingPhone) {
      return;
    }

    const updatedPhones = phones.filter((p) => p.code + p.number !== phoneKey);

    setDeletingPhone(phoneKey);

    try {
      await saveContacts(buildPhonePayload(updatedPhones)).finally(() => {
        setDeletingPhone(null);
      });
      message({ type: 'success', text: 'Номер телефона удалён' });
    } catch (error) {
      message({ type: 'error' });
    }
  };

  return {
    applicantType,
    canDelete,
    deletingPhone,
    isPhoneInList,
    persistNewPhone,
    removePhoneNumber,
  };
};
